// ** React Imports
import { useCallback, useRef, useState, useEffect, useContext } from "react";

// ** Video Player Components
import BigPlayRepeatButton from "../components/BigPlayRepeatButton";
import ControlsSection from "./components/ControlsSection";
import Duration from "../components/Duration";
import SeekBar from "../components/SeekBar";
import VolumeBar from "../components/VolumeBar";

// ** Custom Components
import DynamicVolumeIcon from "components/icons/DynamicVolumeIcon";
import DynamicFullscreenIcon from "components/icons/DynamicFullscreenIcon";
import Text from "components/typography/Text";

// ** Utils & Helpers
import convertTextInModal from "utils/convertTextInModal";

// ** Hooks
import useMobileDetect from "hooks/useMobileDetectHook";
import useVideoPlayer from "./hooks/useVideoPlayer";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// ** Styles
import { OuterVideoContainer, VideoContainer, Video } from "../styles";
import { ControlButtonContainer } from "./styles/ControlButtonContainer.styled";
import { ControlButton } from "./styles/ControlButton.styled";
import { StyledOverlay } from "styles/components/Overlay.styled";
import { StyledSwiperGradient } from "containers/Modal/components/Swipers/styles";

// ##################################

const SwiperVideoPlayer = ({
	source,
	poster,
	contentInfo,
	contentIdUrl,
	renderedWidth,
	renderedHeight,
	correctVideoId,
}) => {
	const { isPhone, isTablet } = useContext(deviceTypeContext);

	const [videoVisible, setVideoVisible] = useState(false);
	const [isDescriptionVisible, setIsDescriptionVisible] = useState(true);

	// ** Referencja do wrappera
	const playerRef = useRef(null);

	// ** Referencja do odtwarzacza 'video' HTML5
	const videoRef = useRef(null);

	const [videoLoaded, setVideoLoaded] = useState(false);

	// ** Hook useMobileDetect
	const mobileDetect = useMobileDetect();

	// ** Sprawdzamy czy użytkownik korzysta z urzadznie z systemem iOS
	const isIos = mobileDetect.isIos();

	// ** Sprawdzamy czy użytkownik korzysta z urzadznia mobilnego
	const isMobile = mobileDetect.isMobile();

	// ** Hook useVideoPlayer
	const {
		playing,
		autoPlay,
		ended,
		togglePlayPause,
		muted,
		toggleMuted,
		volume,
		changeVolume,
		fullscreen,
		toggleFullscreen,
		duration,
		handleOnLoadedData,
		playedSeconds,
		handleOnTimeUpdate,
		played,
		loaded,
		handleSeekChange,
		handleOnProgress,
		handlePlaysInline,
	} = useVideoPlayer({
		videoRef,
		playerRef,
		options: { autoPlay: true, playsInline: true, playsInlineIos: false },
		isIos,
	});

	// ##################################

	// ** Funkcja Renderujaca Pasek Postępu
	// ** Ogladania i Ładowania Filmu
	const renderControlSeekBar = useCallback(
		() => (
			<SeekBar
				loaded={loaded}
				played={played}
				onChange={(e) => handleSeekChange(e.target.value)}
			/>
		),
		[loaded, played]
	);

	// ** Funkcja Renderujaca Pasek Głośności
	const renderControlVolumeBar = useCallback(
		() => (
			<VolumeBar
				volume={muted ? 0 : volume}
				onChange={(e) => changeVolume(e.target.value)}
			/>
		),
		[volume, muted]
	);

	// ** Funkcja Renderujaca Ikonę Głośności / Wyciszenia
	const renderControlVolumeMuteButton = useCallback(() => {
		return (
			<ControlButton
				onClick={() => toggleMuted()}
				customMargin="0.5rem 0 0.5rem 0"
			>
				<DynamicVolumeIcon
					customHeight="17px"
					volume={muted ? 0 : volume}
				/>
			</ControlButton>
		);
	}, [volume, muted]);

	// ** Funkcja obsługujaca ustawianie głośności filmu
	// ** oraz jego wyciszanie 'muted'
	const renderControlVolume = useCallback(
		() => (
			<ControlButtonContainer
				style={
					!isMobile
						? {
								width: "100px",
								padding: "0 0.6rem",
								marginTop: "0.5rem",
						  }
						: { padding: "0", marginTop: "0.5rem" }
				}
			>
				{renderControlVolumeMuteButton()}
				{!isMobile && renderControlVolumeBar()}
			</ControlButtonContainer>
		),
		[volume, muted]
	);

	// ** Funkcja Renderujaca Czas Trwania / Odtwarzania Filmu
	const renderControlDuration = useCallback(
		() => (
			<ControlButtonContainer style={{ marginTop: "0.5rem" }}>
				<Duration seconds={playedSeconds} mr={1} />
				<Text color="whiteAlways">/</Text>
				<Duration seconds={duration} ml={1} />
			</ControlButtonContainer>
		),
		[playedSeconds, duration]
	);

	// ** Funkcja Renderujaca Przycisk Pełnego Ekranu
	const renderControlFullscreenButton = useCallback(
		() => (
			<ControlButtonContainer
				onClick={toggleFullscreen}
				customMargin="0.5rem 1rem 0.5rem 0"
				style={{
					padding: "0 0.75rem 0.25rem 0.75rem",
					cursor: "pointer",
				}}
			>
				<DynamicFullscreenIcon
					customHeight="17px"
					fullscreen={fullscreen}
				/>
			</ControlButtonContainer>
		),
		[fullscreen, toggleFullscreen]
	);

	// ** Funkcja Renderujaca Przycisk Play / Repeat wyświetlany na środku odtwarzacza
	const renderBigPlayRepeatButton = useCallback(() => {
		if (videoVisible && !playing) {
			return (
				<BigPlayRepeatButton
					onClick={togglePlayPause}
					isEnded={ended}
					iconSize={"modal_video_center"}
				/>
			);
		}
		return null;
	}, [videoVisible, playing]);

	useEffect(() => {
		setIsDescriptionVisible(true);

		const timer = setTimeout(() => {
			setIsDescriptionVisible(false);
		}, 3000);

		return () => clearTimeout(timer);
	}, [playing]);

	// ** Zatrzymanie pobierania filmu przy zmianie source (np. po przejściu do następnego slajdu w modalu)
	useEffect(() => {
		const videoElement = videoRef.current;
		return () => {
			if (videoElement) {
				videoElement.pause();
				videoElement.currentTime = 0;
				videoElement.src = "";
				videoElement.load();

				if (videoElement.srcObject) {
					const tracks = videoElement.srcObject.getTracks();
					tracks.forEach((track) => track.stop());
					videoElement.srcObject = null;
				}
			}
			setVideoLoaded(false);
		};
	}, [source.src]);

	// ##################################

	return (
		<OuterVideoContainer ref={playerRef}>
			<VideoContainer $handlePlaysInline={handlePlaysInline()}>
				{renderedWidth && renderedHeight && (
					<>
						<div
							style={{
								backgroundColor: videoLoaded
									? "transparent"
									: "#303030",
								width: `${parseInt(renderedWidth) - 2}px`,
								height: `${parseInt(renderedHeight) - 2}px`,
								position: "absolute",
								top: "50%",
								left: "50%",
								transform: "translate(-50%, -50%)",
							}}
						/>
						<img
							src={poster}
							loading="lazy"
							style={{
								position: "absolute",
								top: "50%",
								left: "50%",
								transform: "translate(-50%, -50%)",
							}}
						/>
					</>
				)}
				{correctVideoId && (
					<>
						{renderBigPlayRepeatButton()}
						<Video
							key={source.src}
							height="100%"
							preload="auto"
							poster={poster}
							muted={isIos ? false : muted}
							playsInline={handlePlaysInline()}
							autoPlay={isIos ? false : autoPlay}
							ref={(el) => {
								videoRef.current = el;
								setVideoVisible(!!el);
							}}
							onLoadedData={() => {
								setVideoLoaded(true);
								handleOnLoadedData();
							}}
							onTimeUpdate={handleOnTimeUpdate}
							onClick={togglePlayPause}
							onProgress={handleOnProgress}
						>
							<source
								src={source.src}
								type={source.type}
								key={source.src}
							/>
						</Video>
					</>
				)}
				{handlePlaysInline() && (
					<>
						<ControlsSection
							right={
								<>
									{renderControlFullscreenButton()}
									{renderControlVolume()}
									{renderControlDuration()}
								</>
							}
						/>
						<StyledOverlay onClick={togglePlayPause} />
					</>
				)}
				{(isPhone || isTablet) &&
					isDescriptionVisible &&
					contentInfo?.data?.descriptionRaw &&
					contentInfo.data.descriptionRaw.length > 0 &&
					contentInfo?.success &&
					Number(contentInfo?.data?.contentId) ===
						Number(contentIdUrl) && (
						<StyledSwiperGradient
							style={{ paddingBottom: "1.25rem" }}
						>
							<Text
								style={{ marginRight: "100px" }}
								color="whiteAlways"
								textBreak
							>
								{convertTextInModal({
									pieces: contentInfo?.data?.description,
								})}
							</Text>
						</StyledSwiperGradient>
					)}
			</VideoContainer>
			{handlePlaysInline() && renderControlSeekBar()}
		</OuterVideoContainer>
	);
};

export default SwiperVideoPlayer;
